import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { LogoutButton } from "./LogoutButton";

export interface UserMenuProps {
  /** Omit to hide the "Profile" entry entirely. */
  onProfile?: () => void;
  onLoggedOut?: () => void;
  className?: string;
}

export function UserMenu({ onProfile, onLoggedOut, className }: UserMenuProps) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  if (!user) return null;

  const label = user.displayName ?? user.username ?? user.email ?? "Account";

  return (
    <div ref={rootRef} className={className ?? "fka-user-menu relative inline-block text-left"}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="fka-user-menu-trigger flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-200 hover:bg-slate-800"
      >
        {user.avatarUrl ? (
          <img src={user.avatarUrl} alt="" className="fka-avatar h-6 w-6 rounded-full object-cover" />
        ) : (
          <span className="fka-avatar-fallback flex h-6 w-6 items-center justify-center rounded-full bg-amber-500 text-xs font-semibold text-slate-950">
            {label.charAt(0).toUpperCase()}
          </span>
        )}
        <span className="fka-user-menu-label">{label}</span>
      </button>

      {open && (
        <div
          role="menu"
          className="fka-user-menu-panel absolute right-0 z-10 mt-2 w-44 rounded-md border border-slate-800 bg-slate-900 py-1 shadow-lg"
        >
          {onProfile && (
            <button
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onProfile();
              }}
              className="fka-user-menu-item block w-full px-3 py-2 text-left text-sm text-slate-200 hover:bg-slate-800"
            >
              Profile
            </button>
          )}
          <LogoutButton
            className="fka-user-menu-item block w-full px-3 py-2 text-left text-sm text-slate-200 hover:bg-slate-800"
            onLoggedOut={() => {
              setOpen(false);
              onLoggedOut?.();
            }}
          />
        </div>
      )}
    </div>
  );
}
